// CHANGE_BACKEND: Immediate launch APIs still expect patient identity in workflow-specific
// shapes. Keep this adapter isolated until backend services accept one normalized patient reference.
// CHANGE_BACKEND: Phone launch endpoints do not yet accept patient identifiers and there is no
// phone-scribe endpoint, so unsupported phone combinations are guarded in the frontend.
import {
  AUTO_DETECT_LABEL,
  AUTO_DETECT_WIRE_VALUE,
  COMMON_DIALECTS,
  COMMON_LANGUAGES,
  SUPPORTED_LANGUAGES,
} from '../../common/languages';
import {isNonEnglish} from '../../constants/designTokens';
import {
  isSupportedVideoCallLink,
  normalizeVideoCallLink,
} from '../../common/videoCallLinks';
import {buildLiveEncounterRoute} from '../../utils/encounterLaunch';
import {
  SCRIBE_ROUTE,
  buildLiveScribeRouteState,
} from '../../utils/scribeLaunch';
import {patientNameFromRecord} from '../../utils/patientNames';
import {
  combinePhoneExtension,
  splitPhoneExtension,
} from '../../utils/phoneExtension';
import {
  plannerPatientFromPatientItem,
  plannerPatientMetadataText,
} from './plannerModels';

export {combinePhoneExtension, splitPhoneExtension};

export const CREATE_SESSION_MIN_SCHEDULE_LEAD_MS = 30 * 60 * 1000;
export const CREATE_SESSION_UNWIRED_TOAST_TITLE = 'Config not wired in yet';

export const CREATE_SESSION_LAUNCH_ACTIONS = {
  ROUTE: 'route',
  CREATE_BOT: 'createBot',
  PHONE_CALL: 'phoneCall',
  UNWIRED: 'unwired',
};

const trimValue = (value: any) => String(value || '').trim();

const firstPresentValue = (...values: any[]) =>
  values.find(candidate => trimValue(candidate)) || '';

export const normalizeTypeaheadText = (value: any) =>
  trimValue(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export const formatCreateSessionPhoneNumber = (value: any): string => {
  // Preserve any trailing extension (e.g. "x123") so the formatter is safe to
  // run over a combined number+extension string; only the dialable number is
  // reformatted as a US 10-digit value.
  const {number, extension} = splitPhoneExtension(value);
  const rawDigits = number.replace(/\D/g, '');
  const digits = (
    rawDigits.length > 10 && rawDigits.startsWith('1')
      ? rawDigits.slice(1)
      : rawDigits
  ).slice(0, 10);
  let formatted: string;
  if (digits.length <= 3) formatted = digits;
  else if (digits.length <= 6)
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  else
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return extension ? combinePhoneExtension(formatted, extension) : formatted;
};

export const patientDisplayName = (item: any) => {
  const name = patientNameFromRecord(item);
  return name.displayName || name.fullName || name.patientName;
};

export const patientLanguage = (item: any) => {
  const localPatient = item?.localPatient || {};
  const ehrPatient = item?.ehrPatient || {};
  const plannerPatient = item?.patient || {};
  return firstPresentValue(
    localPatient.preferred_language,
    localPatient.preferredLanguage,
    localPatient.language,
    ehrPatient.language,
    ehrPatient.preferred_language,
    ehrPatient.preferredLanguage,
    plannerPatient.language,
    plannerPatient.preferred_language,
    plannerPatient.preferredLanguage,
    item?.preferred_language,
    item?.preferredLanguage,
    item?.language,
  );
};

export const patientDialect = (item: any) => {
  const localPatient = item?.localPatient || {};
  const ehrPatient = item?.ehrPatient || {};
  const plannerPatient = item?.patient || {};
  return firstPresentValue(
    localPatient.preferred_dialect,
    localPatient.preferredDialect,
    localPatient.dialect,
    ehrPatient.dialect,
    ehrPatient.preferred_dialect,
    ehrPatient.preferredDialect,
    plannerPatient.dialect,
    plannerPatient.preferred_dialect,
    plannerPatient.preferredDialect,
    item?.preferred_dialect,
    item?.preferredDialect,
    item?.dialect,
  );
};

const normalizeLanguageName = (value: any) =>
  String(value || '')
    .trim()
    .toLowerCase();

// Whether the provider speaks the given language. Matches on the base language
// name, tolerating dialect-qualified patient values (e.g. patient "Mexican
// Spanish" still matches provider "Spanish").
export const providerSpeaksLanguage = (
  language: any,
  providerLanguages: any,
): boolean => {
  const target = normalizeLanguageName(language);
  if (!target) return false;
  const spokenList = Array.isArray(providerLanguages) ? providerLanguages : [];
  return spokenList.some(item => {
    const spoken = normalizeLanguageName(item);
    if (!spoken) return false;
    return (
      spoken === target || target.includes(spoken) || spoken.includes(target)
    );
  });
};

// Decides whether a brand-new encounter should default the interpreter ON for
// the selected patient: only when the patient's preferred language is
// non-English AND the provider does not already speak it. English patients, or
// patients whose language the provider speaks, default to interpreter off
// (scribe), since no interpreter is needed.
export const shouldDefaultInterpreterOn = (
  patientItem: any,
  providerLanguages: any,
): boolean => {
  const language = patientLanguage(patientItem);
  if (!isNonEnglish(language)) return false;
  return !providerSpeaksLanguage(language, providerLanguages);
};

export const patientMrn = (item: any) => {
  const localPatient = item?.localPatient || {};
  const ehrPatient = item?.ehrPatient || {};
  const plannerPatient = item?.patient || {};
  return firstPresentValue(
    localPatient.mrn,
    localPatient.medicalRecordNumber,
    ehrPatient.mrn,
    ehrPatient.medicalRecordNumber,
    ehrPatient.accountNo,
    ehrPatient.account_no,
    plannerPatient.mrn,
    plannerPatient.medicalRecordNumber,
    plannerPatient.accountNo,
    plannerPatient.account_no,
    item?.mrn,
    item?.medicalRecordNumber,
    item?.accountNo,
    item?.account_no,
  );
};

export const patientMetadata = (item: any) => {
  // Tag a purely-local patient (a local record with no EHR side) so it's
  // distinguishable from EHR/linked rows: Language • Local • DOB • MRN.
  const isLocal = Boolean(item?.localPatient) && !item?.ehrPatient;
  return plannerPatientMetadataText(plannerPatientFromPatientItem(item), {
    separator: ' · ',
    sourceLabel: isLocal ? 'Local' : '',
  });
};

export const patientPhone = (item: any) => {
  const localPatient = item?.localPatient || {};
  const ehrPatient = item?.ehrPatient || {};
  const plannerPatient = item?.patient || {};
  return trimValue(
    firstPresentValue(
      localPatient.phone_number,
      localPatient.phoneNumber,
      localPatient.phone,
      ehrPatient.phone,
      ehrPatient.phoneNumber,
      plannerPatient.phone,
      plannerPatient.phoneNumber,
      item?.phone_number,
      item?.phoneNumber,
      item?.phone,
    ),
  );
};

export const patientSearchShape = (item: any) => {
  const localPatient = item?.localPatient || {};
  const ehrPatient = item?.ehrPatient || {};
  const plannerPatient = item?.patient || {};
  const name = patientNameFromRecord(item);
  return {
    first_name: name.firstName,
    middleName: name.middleName,
    last_name: name.lastName,
    displayName: name.displayName,
    patientName: name.patientName,
    mrn: patientMrn(item),
    accountNo: firstPresentValue(
      ehrPatient.accountNo,
      ehrPatient.account_no,
      plannerPatient.accountNo,
      plannerPatient.account_no,
      item?.accountNo,
      item?.account_no,
    ),
    preferred_language: patientLanguage(item),
    dob: firstPresentValue(
      ehrPatient.dob,
      ehrPatient.dateOfBirth,
      plannerPatient.dob,
      plannerPatient.dateOfBirth,
      item?.dob,
      item?.dateOfBirth,
    ),
    date_of_birth: firstPresentValue(
      localPatient.date_of_birth,
      localPatient.dateOfBirth,
      plannerPatient.date_of_birth,
      item?.date_of_birth,
    ),
    id: firstPresentValue(
      localPatient.id,
      ehrPatient.id,
      plannerPatient.id,
      item?.id,
    ),
    ehrSourceId: firstPresentValue(
      ehrPatient.ehrSourceId,
      ehrPatient.ehr_source_id,
      plannerPatient.ehrId,
      plannerPatient.ehr_source_id,
      item?.ehrSourceId,
      item?.ehr_source_id,
    ),
  };
};

export const patientEhrId = (item: any) => {
  const localPatient = item?.localPatient || {};
  const ehrPatient = item?.ehrPatient || {};
  // Do NOT resolve an EHR id from a local patient's `fhir_patient_id` link:
  // local and EHR patients are distinct entities (strict separation), so a
  // local-only assignment must not silently inherit a linked EHR id. This
  // matches ehrService.resolveEhrPatientId, which also excludes fhir_patient_id.
  return (
    ehrPatient.ehrSourceId ||
    ehrPatient.ehr_source_id ||
    ehrPatient.id ||
    localPatient._ehrPatientId ||
    localPatient.ehr_source_id ||
    ''
  );
};

export const resolveCreateSessionPatient = (item: any) => {
  const localPatient = item?.localPatient || null;
  const ehrPatient = item?.ehrPatient || null;
  const name = patientNameFromRecord(item);

  if (localPatient && ehrPatient) {
    return {
      ...localPatient,
      first_name:
        localPatient.first_name || localPatient.firstName || name.firstName,
      middle_name:
        localPatient.middle_name || localPatient.middleName || name.middleName,
      last_name:
        localPatient.last_name || localPatient.lastName || name.lastName,
      preferred_language:
        localPatient.preferred_language || ehrPatient.language || null,
      preferred_dialect: localPatient.preferred_dialect || null,
      mrn: localPatient.mrn || ehrPatient.mrn || null,
      date_of_birth: localPatient.date_of_birth || ehrPatient.dob || null,
      gender: localPatient.gender || ehrPatient.gender || null,
      phone_number: localPatient.phone_number || ehrPatient.phone || null,
      email: localPatient.email || ehrPatient.email || null,
      _ehrPatientId: patientEhrId(item) || null,
      _matchKind: item.matchKind,
    };
  }

  if (localPatient) {
    return {
      ...localPatient,
      first_name:
        localPatient.first_name || localPatient.firstName || name.firstName,
      middle_name:
        localPatient.middle_name || localPatient.middleName || name.middleName,
      last_name:
        localPatient.last_name || localPatient.lastName || name.lastName,
      _ehrPatientId: patientEhrId(item) || localPatient._ehrPatientId || null,
      _matchKind: item.matchKind,
    };
  }

  if (ehrPatient) {
    const ehrId = patientEhrId(item);
    return {
      id: ehrId,
      ehr_source_id: ehrId,
      fhir_patient_id: ehrId || null,
      first_name: name.firstName,
      middle_name: name.middleName || null,
      last_name: name.lastName,
      date_of_birth: ehrPatient.dob || null,
      gender: ehrPatient.gender || null,
      mrn: ehrPatient.mrn || null,
      phone_number: ehrPatient.phone || null,
      email: ehrPatient.email || null,
      preferred_language: ehrPatient.language || null,
      _ehrPatientId: ehrId || null,
      _matchKind: item.matchKind,
    };
  }

  return null;
};

const createSessionPatientIdentity = (patientItem: any) => {
  const patient = resolveCreateSessionPatient(patientItem);
  if (!patient) {
    return {
      patient: null,
      patientId: null,
      ehrPatientId: null,
      patientName: patientDisplayName(patientItem) || '',
      patientPhone: patientPhone(patientItem) || '',
    };
  }

  const patientName =
    [patient.first_name, patient.last_name].filter(Boolean).join(' ').trim() ||
    patientDisplayName(patientItem) ||
    '';

  return {
    patient,
    patientId: patientItem?.localPatient?.id || null,
    ehrPatientId: patient._ehrPatientId || patientEhrId(patientItem) || null,
    patientName,
    patientPhone: patient.phone_number || '',
  };
};

const buildCreateSessionContext = (draft: any) => ({
  identity: createSessionPatientIdentity(draft?.patient?.item),
  interpreterEnabled: Boolean(draft?.capability?.interpreterEnabled),
  language: trimValue(draft?.capability?.language),
  dialect: trimValue(draft?.capability?.dialect),
  locationType: draft?.location?.type,
  alreadyWithPatient: Boolean(draft?.location?.alreadyWithPatient),
  // How the provider joins a phone encounter when the patient is NOT already
  // on the line: 'web' = start the LiveKit room on this device and SIP-dial the
  // patient into it; 'phone' = the legacy phone-based flow (three-way for
  // interpreter, single-leg for scribe).
  phoneJoinMode: draft?.location?.phoneJoinMode === 'phone' ? 'phone' : 'web',
  callerId: trimValue(draft?.location?.callerId),
});

// True when a phone encounter should start on the web and dial the patient out
// (provider not already on the line + web join mode).
export const isWebDialOutContext = (context: Record<string, any>): boolean =>
  context.locationType === 'phone' &&
  !context.alreadyWithPatient &&
  context.phoneJoinMode === 'web';

const buildCreateSessionPlannerOriginState = (draft: any) => {
  if (!draft?.navigation?.fromPlanner) return {};
  return {
    fromPlanner: true,
    ...(draft.navigation.restorePatientItem
      ? {
          restorePatientItem: draft.navigation.restorePatientItem,
        }
      : {}),
  };
};

const buildCreateSessionRouteState = (
  draft: any,
  context = buildCreateSessionContext(draft),
) => ({
  ...context.identity,
  language: context.language,
  dialect: context.dialect,
  ...buildCreateSessionPlannerOriginState(draft),
});

export const buildCreateSessionBotPayload = (
  draft: any,
  context = buildCreateSessionContext(draft),
) => {
  const {identity, interpreterEnabled} = context;
  const payload: Record<string, any> = {
    mode: interpreterEnabled ? 'interpreter' : 'scribe',
    meetingURL: normalizeVideoCallLink(draft?.location?.videoCallLink),
  };

  if (interpreterEnabled) {
    payload.language = context.language;
    payload.dialect = context.dialect;
  }

  if (identity.patientId) {
    payload.patientId = identity.patientId;
  }
  if (identity.ehrPatientId) {
    payload.ehrPatientId = identity.ehrPatientId;
  }
  if (identity.patientName) {
    payload.patientName = identity.patientName;
  }

  return payload;
};

export const buildCreateSessionPhoneState = (
  draft: any,
  context = buildCreateSessionContext(draft),
) => ({
  ...buildCreateSessionRouteState(draft, context),
  patientPhone:
    trimValue(draft?.location?.patientPhone) || context.identity.patientPhone,
  providerPhone: trimValue(draft?.location?.providerPhone),
  alreadyWithPatient: context.alreadyWithPatient,
  interpreterEnabled: context.interpreterEnabled,
  phoneJoinMode: context.phoneJoinMode,
  callerId: context.callerId,
});

// Patient phone + identity to dial out to once a web room connects. Used by the
// /encounter and /scribe web dial-out launch states.
const buildCreateSessionDialOutState = (
  draft: any,
  context = buildCreateSessionContext(draft),
) => ({
  dialOutPhoneNumber:
    trimValue(draft?.location?.patientPhone) || context.identity.patientPhone,
  dialOutRole: 'patient',
  dialOutDisplayName: context.identity.patientName || '',
  ...(context.callerId ? {callerId: context.callerId} : {}),
});

export const buildCreateSessionPhoneCallPayloadFromState = (
  phoneState: Record<string, any> = {},
  userData: Record<string, any> = {},
) => ({
  patientPhoneNumber: phoneState.patientPhone,
  providerPhoneNumber: phoneState.providerPhone,
  language: phoneState.language,
  dialect: phoneState.dialect,
  userId: trimValue(userData?.userId),
  facilityId: trimValue(userData?.facilityId),
});

export const buildCreateSessionLaunchIntent = (draft: any) => {
  const context = buildCreateSessionContext(draft);

  if (context.locationType === 'inPerson') {
    if (!context.interpreterEnabled) {
      return {
        action: CREATE_SESSION_LAUNCH_ACTIONS.ROUTE,
        path: SCRIBE_ROUTE,
        state: buildLiveScribeRouteState({
          patient: context.identity.patient,
          state: buildCreateSessionPlannerOriginState(draft),
        }),
      };
    }

    const encounterRoute = buildLiveEncounterRoute(
      buildCreateSessionRouteState(draft, context),
    );
    return {
      action: CREATE_SESSION_LAUNCH_ACTIONS.ROUTE,
      path: encounterRoute.path,
      state: encounterRoute.state,
    };
  }

  if (context.locationType === 'video') {
    return {
      action: CREATE_SESSION_LAUNCH_ACTIONS.CREATE_BOT,
      payload: buildCreateSessionBotPayload(draft, context),
    };
  }

  if (context.locationType === 'phone') {
    // Web dial-out: start the LiveKit room on this device and SIP-dial the
    // patient into it. Routes to the live encounter (interpreter) or scribe
    // view, which dial after connecting.
    if (isWebDialOutContext(context)) {
      const dialOut = buildCreateSessionDialOutState(draft, context);
      if (context.interpreterEnabled) {
        const encounterRoute = buildLiveEncounterRoute(
          buildCreateSessionRouteState(draft, context),
        );
        return {
          action: CREATE_SESSION_LAUNCH_ACTIONS.ROUTE,
          path: encounterRoute.path,
          state: {...encounterRoute.state, ...dialOut},
        };
      }
      return {
        action: CREATE_SESSION_LAUNCH_ACTIONS.ROUTE,
        path: SCRIBE_ROUTE,
        state: buildLiveScribeRouteState({
          patient: context.identity.patient,
          state: {...buildCreateSessionPlannerOriginState(draft), ...dialOut},
        }),
      };
    }

    // Phone supports both interpreter (single-leg or provider-first three-way)
    // and the silent telephonic scribe; the launcher branches on
    // interpreterEnabled + alreadyWithPatient in the phone state.
    return {
      action: CREATE_SESSION_LAUNCH_ACTIONS.PHONE_CALL,
      state: buildCreateSessionPhoneState(draft, context),
    };
  }

  return {
    action: CREATE_SESSION_LAUNCH_ACTIONS.UNWIRED,
    toastTitle: CREATE_SESSION_UNWIRED_TOAST_TITLE,
  };
};

const buildLanguageOptions = () => {
  const commonRankByLanguage = new Map(
    COMMON_LANGUAGES.map((languageName, index) => [languageName, index]),
  );
  const fallbackRank = COMMON_LANGUAGES.length + 1;

  return Object.keys(SUPPORTED_LANGUAGES)
    .map(languageName => ({
      id: languageName,
      language: languageName,
      label: languageName,
      dialectCount: SUPPORTED_LANGUAGES[languageName]?.length || 0,
      isCommon: commonRankByLanguage.has(languageName),
      commonRank: commonRankByLanguage.get(languageName) ?? fallbackRank,
    }))
    .map(option => ({
      ...option,
      searchText: normalizeTypeaheadText(option.language),
    }))
    .sort((left, right) => {
      if (left.commonRank !== right.commonRank) {
        return left.commonRank - right.commonRank;
      }
      return left.label.localeCompare(right.label);
    });
};

export const LANGUAGE_OPTIONS = buildLanguageOptions();

// Pinned above every group in the language typeahead (never partitioned into
// Favorites / All languages). Selecting it sends the 'auto' wire value; the
// interpreter agent detects the patient's language from their speech.
// Temporarily offered only to superadmins (see useCreateSessionLanguageFields).
export const AUTO_DETECT_LANGUAGE_OPTION = {
  id: AUTO_DETECT_WIRE_VALUE,
  language: AUTO_DETECT_WIRE_VALUE,
  label: AUTO_DETECT_LABEL,
  dialectCount: 0,
  isCommon: false,
  commonRank: -1,
  isAutoDetect: true,
  searchText: normalizeTypeaheadText(
    `${AUTO_DETECT_LABEL} automatic detect language`,
  ),
};

export const buildDialectOptions = (languageName: any) =>
  (SUPPORTED_LANGUAGES[languageName] || []).map(dialectName => ({
    id: `${languageName}::${dialectName}`,
    dialect: dialectName,
    label: dialectName,
    isCommon: COMMON_DIALECTS[languageName] === dialectName,
    searchText: normalizeTypeaheadText(dialectName),
  }));

export function buildCreateSessionDraft({
  patientItem = null,
  patientSkipped = false,
  patientChoiceRequired = false,
  interpreterEnabled = false,
  language = '',
  dialect = '',
  locationType = 'inPerson',
  patientPhone = '',
  providerPhone = '',
  alreadyWithPatient = true,
  phoneJoinMode = 'web',
  callerId = '',
  videoCallLink = '',
  fromPlanner = false,
  restorePatientItem = null,
}: Record<string, any> = {}) {
  return {
    patient: patientItem
      ? {
          item: patientItem,
          name: patientDisplayName(patientItem) || '',
          mrn: patientMrn(patientItem) || '',
          language: patientLanguage(patientItem) || '',
        }
      : null,
    patientSkipped: Boolean(patientSkipped),
    patientChoiceRequired: Boolean(patientChoiceRequired),
    capability: {
      interpreterEnabled: Boolean(interpreterEnabled),
      language: interpreterEnabled ? trimValue(language) : '',
      dialect: interpreterEnabled ? trimValue(dialect) : '',
    },
    location: {
      type: locationType,
      patientPhone: locationType === 'phone' ? trimValue(patientPhone) : '',
      providerPhone: locationType === 'phone' ? trimValue(providerPhone) : '',
      // ON: provider is already with the patient (single-leg call to the
      // provider). OFF: provider-first three-way that also dials the patient.
      alreadyWithPatient:
        locationType === 'phone' ? Boolean(alreadyWithPatient) : false,
      // Only meaningful for a phone encounter where the patient is not already
      // on the line; defaults to 'web' (start on this device, dial the patient).
      phoneJoinMode:
        locationType === 'phone' && phoneJoinMode === 'phone' ? 'phone' : 'web',
      // Verified outbound caller ID (E.164) to present to the patient on a web
      // dial-out. Empty = the default Opalite number.
      callerId: locationType === 'phone' ? trimValue(callerId) : '',
      videoCallLink:
        locationType === 'video' ? normalizeVideoCallLink(videoCallLink) : '',
    },
    navigation: {
      fromPlanner: Boolean(fromPlanner),
      restorePatientItem: restorePatientItem || null,
    },
  };
}

export function validateCreateSessionDraft(draft: any) {
  const errors: Record<string, string> = {};

  if (
    draft?.patientChoiceRequired &&
    !draft.patient?.item &&
    !draft.patientSkipped
  ) {
    errors.patient = 'Select a patient or choose no patient.';
  }

  if (draft?.capability?.interpreterEnabled) {
    if (!draft.capability.language) {
      errors.language = 'Select a language.';
    }
  }

  if (draft?.location?.type === 'phone') {
    const isWebDialOut =
      !draft.location.alreadyWithPatient &&
      draft.location.phoneJoinMode !== 'phone';

    if (isWebDialOut) {
      // Web dial-out: the provider joins on this device, so only the patient's
      // number is required (no provider phone).
      if (!draft.location.patientPhone) {
        errors.patientPhone = 'Enter the patient number.';
      }
    } else {
      if (!draft.location.providerPhone) {
        errors.providerPhone = 'Enter your number.';
      }
      // The patient number is only needed for the interpreter three-way
      // auto-merge (provider NOT already with the patient). The scribe is always
      // single-leg, so it never needs the patient number.
      const needsPatientForThreeWay =
        draft?.capability?.interpreterEnabled &&
        !draft.location.alreadyWithPatient;
      if (needsPatientForThreeWay && !draft.location.patientPhone) {
        errors.patientPhone = 'Enter the patient number.';
      }
    }
  }

  if (draft?.location?.type === 'video') {
    if (!draft.location.videoCallLink) {
      errors.videoCallLink = 'Enter a video call link.';
    } else if (!isSupportedVideoCallLink(draft.location.videoCallLink)) {
      errors.videoCallLink = 'Enter a supported video call link.';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

export const defaultCreateSessionJoinAt = () => {
  const date = new Date(Date.now() + CREATE_SESSION_MIN_SCHEDULE_LEAD_MS);
  date.setSeconds(0, 0);
  if (date.getTime() - Date.now() < CREATE_SESSION_MIN_SCHEDULE_LEAD_MS) {
    date.setMinutes(date.getMinutes() + 1);
  }
  return date;
};

export const firstValidationMessage = (errors: any): string =>
  (Object.values(errors || {}).find(Boolean) as string) ||
  'Complete the required fields.';
